import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/api';
import { useToast } from '@/contexts/ToastContext';
import type { Notification } from './useApi';

interface NotificationResponse {
  notifications: Notification[];
  unreadCount: number;
}

/**
 * Polls for unread notifications and shows toast when new ones arrive
 * Returns the current unread count for display in the sidebar badge
 */
export function useNotificationPolling() {
  const { addToast } = useToast();
  const previousCountRef = useRef<number | null>(null);
  const previousNotificationIdsRef = useRef<Set<string>>(new Set());

  const { data } = useQuery({
    queryKey: ['notifications-polling'],
    queryFn: () => api.get<NotificationResponse>('/notifications?limit=10&unreadOnly=true'),
    refetchInterval: 30_000, // Poll every 30 seconds
    refetchOnWindowFocus: true,
  });

  const unreadCount = data?.unreadCount ?? 0;
  const notifications = data?.notifications ?? [];

  useEffect(() => {
    // Skip on first load
    if (previousCountRef.current === null) {
      previousCountRef.current = unreadCount;
      previousNotificationIdsRef.current = new Set(notifications.map(n => n.notificationId));
      return;
    }

    // Check for new notifications
    const currentNotificationIds = new Set(notifications.map(n => n.notificationId));
    const newNotifications = notifications.filter(
      n => !previousNotificationIdsRef.current.has(n.notificationId)
    );

    // Show toast for new notifications
    if (newNotifications.length > 0) {
      // Show toast for the most recent new notification
      const mostRecent = newNotifications[0];

      // Determine toast type based on notification type
      let toastType: 'info' | 'warning' | 'error' | 'success' = 'info';
      if (mostRecent.type === 'deadline_overdue' || mostRecent.type === 'workflow_failed') {
        toastType = 'error';
      } else if (mostRecent.type === 'deadline_approaching' || mostRecent.type === 'compliance_issue') {
        toastType = 'warning';
      } else if (mostRecent.type === 'workflow_completed') {
        toastType = 'success';
      }

      addToast({
        type: toastType,
        title: mostRecent.title,
        message: newNotifications.length > 1
          ? `${mostRecent.message} (+${newNotifications.length - 1} more)`
          : mostRecent.message,
        duration: 5000,
      });
    }

    // Update refs
    previousCountRef.current = unreadCount;
    previousNotificationIdsRef.current = currentNotificationIds;
  }, [unreadCount, notifications, addToast]);

  return { unreadCount };
}

/**
 * Hook to get just the unread notification count
 * Use this in components that only need the count (like the sidebar badge)
 */
export function useUnreadNotificationCount() {
  const { unreadCount } = useNotificationPolling();
  return unreadCount;
}
